import React, { useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  HeartPulse, Stethoscope, Scissors, ShoppingBag, Sparkles, ShieldCheck,
  ChevronRight, Clock, PhoneCall, ArrowUpRight
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SEO from '../components/SEO';

gsap.registerPlugin(ScrollTrigger);

const ServicesPage = () => {
  const containerRef = useRef(null);


  const serviceCategories = [
    {
      title: "Pet Consultation",
      subtitle: "Professional Diagnosis",
      desc: "Professional veterinary consultation for accurate diagnosis and treatment planning.",
      icon: <Stethoscope size={24} />,
      accentColor: "bg-primary",
      glowColor: "bg-primary/10",
      path: "/consultation",
      items: [
        { name: "Walk-In Welcome", desc: "Open consultations without prior appointments for convenience.", path: "/consultation" },
        { name: "Wellness Exams", desc: "Preventive health checks to catch problems early.", path: "/consultation" },
        { name: "Personalized Treatment", desc: "Tailored care plans based on breed, age, and history.", path: "/consultation" },
        { name: "Expert Diagnosis", desc: "Accurate clinical evaluation by experienced veterinarians.", path: "/diagnostic-services" }
      ],
      image: "/images/services/consultation_wellness.png",
      badge: "Expert Care"
    },
    {
      title: "Pet Vaccination",
      subtitle: "Preventive Health Programs",
      desc: "Protect your pets with regular vaccination and preventive health programs to ensure long-term immunity.",
      icon: <HeartPulse size={24} />,
      accentColor: "bg-red-500",
      glowColor: "bg-red-500/10",
      path: "/critical-care",
      items: [
        { name: "24/7 Critical Care", desc: "Round-the-clock emergency response for life-threatening conditions.", path: "/critical-care" },
        { name: "Pet Ambulance", desc: "Dedicated ambulance service for rapid in-field response.", path: "/critical-care" },
        { name: "Advanced Equipment", desc: "ICU-grade monitoring and life support systems.", path: "/critical-care" },
        { name: "Trauma Stabilization", desc: "Immediate stabilization protocols for accident victims.", path: "/critical-care" }
      ],
      image: "/images/services/emergency_critical_care.png",
      badge: "24/7 Response"
    },
    {
      title: "Surgeries",
      subtitle: "Surgical Excellence",
      desc: "From routine spaying to complex orthopedic and tumor surgeries, our advanced surgical suite ensures the safest possible care for your pet.",
      icon: <Scissors size={24} />,
      accentColor: "bg-amber-500",
      glowColor: "bg-amber-500/10",
      path: "/surgeries",
      items: [
        { name: "Routine Spaying & Neutering", desc: "Safe and effective sterilization with full post-op care.", path: "/surgeries" },
        { name: "Orthopedic Surgery", desc: "Bone fracture repair, joint corrections & limb surgeries.", path: "/surgeries" },
        { name: "Tumor & Mass Removal", desc: "Precision oncological surgery with pathology support.", path: "/surgeries" },
        { name: "Sterile Operating Theater", desc: "High-sterility OT ensuring minimal infection risk.", path: "/surgeries" }
      ],
      image: "/images/services/surgical_theater.png",
      badge: "Surgical Suite"
    },
    {
      title: "Veterinary Diagnostics",
      subtitle: "Advanced Testing Facilities",
      desc: "We offer advanced testing facilities including blood analysis, X-Ray imaging, and Ultrasound scanning for effective treatment.",
      icon: <Sparkles size={24} />,
      accentColor: "bg-blue-500",
      glowColor: "bg-blue-500/10",
      path: "/diagnostic-services",
      items: [
        { name: "In-House Lab", desc: "Complete blood investigations and microscopic analysis.", path: "/diagnostic-services" },
        { name: "Digital X-Rays", desc: "High-resolution radiography for skeletal and soft tissue.", path: "/xray" },
        { name: "Ultrasound Imaging", desc: "Real-time abdominal and cardiac ultrasound scans.", path: "/diagnostic-services" },
        { name: "ECG Monitoring", desc: "Cardiac function evaluation for heart health assessment.", path: "/monitoring" }
      ],
      image: "/images/services/diagnostics_lab.png",
      badge: "Clinical Labs"
    },
    {
      title: "Pet Grooming",
      subtitle: "Hygiene & Maintenance",
      desc: "Keep your pets clean, healthy, and comfortable with our professional grooming services including bathing and nail trimming.",
      icon: <ShoppingBag size={24} />,
      accentColor: "bg-green-500",
      glowColor: "bg-green-500/10",
      path: "/grooming",
      items: [
        { name: "Full Coat Grooming", desc: "Breed-specific styling and sanitary grooming sessions.", path: "/grooming" },
        { name: "Medicated Baths", desc: "Therapeutic baths for mange, scabies, and skin health.", path: "/grooming" },
        { name: "Parasite Management", desc: "Anti-tick and flea treatment with protective coats.", path: "/preventive-care" },
        { name: "Nail & Ear Care", desc: "Professional nail trimming and ear cleaning services.", path: "/grooming" }
      ],
      image: "/images/services/pet_grooming.png",
      badge: "Spa & Care"
    },
    {
      title: "Pet Boarding",
      subtitle: "Safe & Hygienic Supervision",
      desc: "Safe and hygienic pet boarding with proper care and supervision for complete peace of mind.",
      icon: <ShieldCheck size={24} />,
      accentColor: "bg-purple-500",
      glowColor: "bg-purple-500/10",
      path: "/boarding",
      items: [
        { name: "24/7 CCTV Supervision", desc: "Round-the-clock monitoring for complete peace of mind.", path: "/boarding" },
        { name: "AC Comfort Rooms", desc: "Temperature-controlled private boarding suites.", path: "/boarding" },
        { name: "Fresh Meals & Nutrition", desc: "Customized diet and fresh meal plans during the stay.", path: "/boarding" },
        { name: "Indoor Play & Socialization", desc: "Dedicated play zones and safe supervised interactions.", path: "/boarding" }
      ],
      image: "/images/services/pet_boarding.png",
      badge: "Pet Hotel"
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.svc-hero-line', { y: 60, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2 });
      gsap.utils.toArray('.service-category-row').forEach((section) => {
        gsap.fromTo(section.querySelectorAll('.cat-animate'), { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, stagger: 0.07, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 80%', toggleActions: 'play none none reverse' }
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <motion.div ref={containerRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="bg-white overflow-x-hidden">
      <SEO 
        title="Veterinary Services in Vanasthalipuram, Hyderabad"
        description="Discover comprehensive veterinary care in Vanasthalipuram including consultation, vaccination, diagnostics, grooming, and boarding at Dr. Suresh Pet Care Hospital."
        canonical="/services"
      />


      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative pt-[200px] md:pt-[160px] pb-20 overflow-hidden min-h-[80vh] flex flex-col justify-center">
        <div className="absolute inset-0 z-0">
          <img src="/images/lsb-machine.jpeg" alt="" className="w-full h-full object-cover opacity-[0.04] blur-2xl scale-110" />
          <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/5" />
        </div>
        <div className="absolute top-[20%] right-[5%] w-[400px] h-[400px] bg-primary/6 rounded-full blur-[120px] pointer-events-none" />

        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="svc-hero-line inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/15">
                <Sparkles size={13} className="text-primary" />
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">Our Capabilities</span>
              </div>
              <h1 className="svc-hero-line text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(3rem,8vw,6rem)]">
                Best Dog Clinic for <br />
                <em className="font-normal text-primary not-italic">Expert Clinical Care.</em>
              </h1>
              <p className="svc-hero-line text-muted-foreground text-xl md:text-2xl leading-relaxed max-w-2xl">
                Professional canine healthcare and surgical excellence in Hyderabad. Guided by 23+ years of surgical mastery by Dr. T Suresh Babu.
              </p>
              <div className="svc-hero-line flex gap-4">
                <a href="https://wa.me/919848954921" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-bold text-sm hover:shadow-[0_20px_40px_rgba(3,137,3,0.3)] transition-all duration-500">
                  Book Consultation
                </a>
                <a href="tel:+919848954921"
                  className="inline-flex items-center gap-3 bg-navy/5 text-navy px-8 py-4 rounded-full font-bold text-sm border border-navy/10 hover:bg-navy/10 transition-all">
                  <PhoneCall size={16} className="text-primary" /> Emergency
                </a>
              </div>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2, ease: [0.16,1,0.3,1] }} className="relative group hidden lg:block">
              <div className="relative rounded-[4rem] overflow-hidden shadow-2xl border border-navy/5 aspect-[4/5]">
                <img src="/images/lsb-machine.jpeg" alt="Dr. Suresh Pet Care Advanced Clinical Equipment" className="w-full h-full object-cover transition-transform duration-[10s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl">
                23+ Years of<br />Surgical Excellence
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID ─────────────────────────────────── */}
      <section className="py-8">
        <div className="section-container space-y-24 lg:space-y-32">
          {serviceCategories.map((cat, i) => (
            <div key={i} className={`service-category-row flex flex-col lg:items-center gap-16 lg:gap-24 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
              {/* Content */}
              <div className="lg:w-1/2 space-y-10">
                <div className="cat-animate space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-12 h-12 rounded-2xl ${cat.accentColor} flex items-center justify-center text-white shadow-lg`}>
                      {cat.icon}
                    </div>
                    <span className="px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-navy text-white">{cat.badge}</span>
                  </div>
                  <span className="block text-primary font-black tracking-[0.3em] uppercase text-[10px]">{cat.subtitle}</span>
                  <h2 className="text-navy font-bold text-5xl md:text-6xl leading-[0.92] tracking-tight">{cat.title}</h2>
                  <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">{cat.desc}</p>
                </div>

                <div className="cat-animate grid sm:grid-cols-2 gap-4">
                  {cat.items.map((item, j) => (
                    <Link to={item.path} key={j} className="group/item block p-6 bg-[#f8faff] border border-navy/5 rounded-[2rem] hover:border-primary/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-500">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-navy text-sm group-hover/item:text-primary transition-colors">{item.name}</h3>
                        <div className="w-7 h-7 rounded-full bg-primary/5 flex items-center justify-center group-hover/item:bg-primary group-hover/item:text-white transition-all">
                          <ChevronRight size={12} />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </Link>
                  ))}
                </div>

                <Link to={cat.path} className="cat-animate inline-flex items-center gap-3 text-primary font-bold text-sm hover:gap-5 transition-all duration-500 group">
                  Explore Full Protocol <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              {/* Image */}
              <div className="cat-animate lg:w-1/2">
                <div className="relative">
                  <div className="relative rounded-[4rem] overflow-hidden border border-navy/5 aspect-[4/3] shadow-2xl group">
                    <img src={cat.image} alt={`Dr. T Suresh Babu ${cat.title} — Best Dog Clinic in Hyderabad`}
                      className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
                  </div>
                  <div className={`absolute -top-4 -right-4 w-32 h-32 ${cat.glowColor} rounded-full blur-[60px] pointer-events-none`} />
                  {/* Floating badge */}
                  <div className="absolute -bottom-5 -right-5 bg-white border border-border shadow-xl p-6 rounded-3xl hidden md:block">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${cat.accentColor} flex items-center justify-center text-white`}><ShieldCheck size={18} /></div>
                      <div>
                        <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Quality</p>
                        <p className="text-base font-bold text-navy">Certified</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── EMERGENCY CTA ─────────────────────────────────── */}
      <section className="py-24">
        <div className="section-container">
          <div className="relative rounded-[4rem] overflow-hidden bg-[#010816] p-12 md:p-20 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="absolute top-0 right-0 w-[40%] h-full bg-primary/8 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[30%] h-[80%] bg-blue-900/10 blur-[100px] pointer-events-none" />

            <div className="space-y-6 relative z-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                <Clock size={13} className="text-primary animate-pulse" />
                <span className="text-[10px] font-black tracking-[0.3em] uppercase text-white/60">Emergency Protocol</span>
              </div>
              <h3 className="text-4xl md:text-6xl text-white font-bold leading-tight">
                Need Urgent <br /><em className="font-normal text-primary not-italic">Assistance?</em>
              </h3>
              <p className="text-white/50 text-lg max-w-md mx-auto lg:mx-0">
                Our critical response team is on standby for emergency surgeries and trauma stabilisation.
              </p>
              <a href="tel:+919848954921" className="inline-flex items-center gap-3 bg-white text-navy px-10 py-4 rounded-full font-black text-sm tracking-widest hover:shadow-2xl transition-all duration-500">
                <PhoneCall size={16} /> CALL NOW
              </a>
            </div>

            <div className="grid grid-cols-2 gap-6 relative z-10 shrink-0">
              <div className="p-8 bg-white/5 border border-white/10 rounded-3xl space-y-2">
                <p className="text-5xl font-bold text-white">23y+</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Clinical Legacy</p>
              </div>
              <div className="p-8 bg-white/5 border border-white/10 rounded-3xl space-y-2">
                <p className="text-5xl font-bold text-primary">24/7</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">Emergency</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-10 opacity-30">
        <div className="section-container text-center">
          <p className="text-[9px] font-bold uppercase tracking-[0.4em] max-w-3xl mx-auto leading-relaxed">
            Dr. Suresh Pet Care Hospital and Surgical Center — The Best Veterinary Clinic in Hyderabad. Directed by Dr. T Suresh Babu. Specializing in Dog Health, Cat Medicine, and Emergency Canine Surgery.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ServicesPage;
